import { useCallback, useState } from 'react';
import { Form, Modal, message } from 'antd';
import { runAiAction } from '../../services/aiActionApi';
import { applyScalarFieldValues, buildAddRowInitial } from './applyGroupValues';

function stripHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = String(html ?? '');
  return div.textContent || div.innerText || '';
}

function applyTransform(value, transform) {
  if (!transform) return value;
  if (transform === 'stripHtml') return stripHtml(value);
  return value;
}

function wordCount(text) {
  return String(text ?? '').trim().split(/\s+/).filter(Boolean).length;
}

function rowHasData(row) {
  return Object.values(row || {}).some((v) => v !== undefined && v !== null && v !== '');
}

// isInputSatisfied — an input "counts" once it has real content: a file
// present for kind "file", or non-empty (post-transform) text for kind
// "text" that also meets the input's own MinWords, if configured.
function isInputSatisfied(input, rawValue) {
  if (input.kind === 'file') {
    return Array.isArray(rawValue) ? rawValue.length > 0 : Boolean(rawValue);
  }
  const text = applyTransform(rawValue, input.transform);
  const str = String(text ?? '').trim();
  if (!str) return false;
  const minWords = Number(input.minWords) || 0;
  return minWords > 0 ? wordCount(str) >= minWords : true;
}

// computeActionEnabled — an action is disabled until every GATED input is
// satisfied. An input gates the button when it's marked Required, or has a
// MinWords requirement (>0) — a plain optional input never gates. Inputs
// sharing a Group (e.g. "either a JD file or JD text") gate as one unit: the
// group is satisfied once ANY member of it is satisfied.
function computeActionEnabled(action, getValue) {
  const inputs = action.inputs || [];
  if (inputs.length === 0) return true;

  const groupsMap = new Map();
  inputs.forEach((input, i) => {
    const key = input.group || `__solo_${i}`;
    if (!groupsMap.has(key)) groupsMap.set(key, []);
    groupsMap.get(key).push(input);
  });

  for (const groupInputs of groupsMap.values()) {
    const gates = groupInputs.some((i) => i.required || Number(i.minWords) > 0);
    if (!gates) continue;
    const satisfied = groupInputs.some((i) => isInputSatisfied(i, getValue(i.sourceField)));
    if (!satisfied) return false;
  }
  return true;
}

const AI_ACTION_POSITIONS = ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'];
const DEFAULT_AI_ACTION_POSITION = 'bottom-left';

// groupActionsByPosition — buckets a field's click-triggered AI actions by
// their admin-configured Position (blank/unknown falls back to the original
// "bottom-left" layout), so the caller can render each bucket in its own
// slot around the field with the right alignment.
export function groupActionsByPosition(actions = []) {
  const buckets = {};
  actions.forEach((action) => {
    const pos = AI_ACTION_POSITIONS.includes(action.position) ? action.position : DEFAULT_AI_ACTION_POSITION;
    (buckets[pos] ??= []).push(action);
  });
  return buckets;
}

export { AI_ACTION_POSITIONS };

/**
 * useAiActions — shared logic behind config-driven "AI Action" buttons
 * (Generate/Parse-style: send some form fields to an admin-configured
 * service, get back the SAME `{groups: [...]}` shape getFormGroups returns
 * for Edit prefill, and apply it to the form via the same shared functions).
 * Used identically by AddFormV1 and EditFormV1 so the apply logic isn't
 * duplicated.
 *
 * @param {Object} args
 * @param {import('antd').FormInstance} args.form
 * @param {Array} args.groups - the module's form groups (for input lookups)
 * @param {string} args.module
 */
export default function useAiActions({ form, groups, module }) {
  const [loadingKey, setLoadingKey] = useState(null);
  // Re-renders whenever ANY field changes, so isActionEnabled below reflects
  // live typing/upload without needing to know in advance which field names
  // any given action reads from (fully config-driven, no hardcoded paths).
  const watchedValues = Form.useWatch((values) => values, form);

  const isActionEnabled = useCallback(
    (action) => computeActionEnabled(action, (sourceField) => (watchedValues || {})[sourceField]),
    [watchedValues],
  );

  // Returns whether anything was actually applied to the form, so the caller
  // can show an accurate success vs. "nothing found" message — generic to any
  // action/module, since it only looks at what the response itself contained.
  const applyResponse = useCallback((responseGroups) => {
    if (!Array.isArray(responseGroups) || responseGroups.length === 0) return false;

    const scalarValues = applyScalarFieldValues(form, responseGroups.filter((g) => !g.addRow));
    let appliedAny = Object.keys(scalarValues).length > 0;

    responseGroups.forEach((group) => {
      if (!group.addRow) return;
      if (!Array.isArray(group.rows) || group.rows.length === 0) return;

      const shapedRows = buildAddRowInitial(group);
      if (shapedRows.length === 0) return;
      appliedAny = true;

      // A mounted Form.List's field name is already registered in the Form's
      // store (even with an empty array) — setFieldsValue is what antd
      // documents for a post-mount update; it re-renders the List with the
      // new rows directly, no remount trick needed.
      const applyRows = () => form.setFieldsValue({ [group.name]: shapedRows });

      const existingRows = form.getFieldValue(group.name) || [];
      if (existingRows.some(rowHasData)) {
        Modal.confirm({
          title: 'Replace existing rows?',
          content: `This will replace ${existingRows.length} existing row(s) in "${group.label || group.name}".`,
          okText: 'Replace',
          cancelText: 'Cancel',
          onOk: applyRows,
        });
      } else {
        applyRows();
      }
    });

    return appliedAny;
  }, [form]);

  const runAction = useCallback(async (groupName, field, action, extraFile) => {
    setLoadingKey(action.key);
    try {
      const inputs = {};
      const files = {};

      (action.inputs || []).forEach((input) => {
        if (input.kind === 'file') {
          const fromFileList = form.getFieldValue(input.sourceField);
          const file = extraFile ?? fromFileList?.[fromFileList.length - 1]?.originFileObj;
          if (file) files[input.param] = file;
        } else {
          const raw = form.getFieldValue(input.sourceField);
          if (raw !== undefined && raw !== null && raw !== '') {
            inputs[input.param] = applyTransform(raw, input.transform);
          }
        }
      });
      // Within a shared input Group (e.g. "either a JD file or JD text"), a
      // file input wins — drop the sibling text input if both are present.
      (action.inputs || []).forEach((input) => {
        if (!input.group || input.kind !== 'text') return;
        const fileSiblingProvided = (action.inputs || [])
          .some((i) => i.group === input.group && i.kind === 'file' && files[i.param]);
        if (fileSiblingProvided) delete inputs[input.param];
      });

      const result = await runAiAction({
        module, group: groupName, field: field.field, actionKey: action.key, inputs, files,
      });
      const appliedAny = applyResponse(result?.groups);
      const label = action.label || 'AI action';
      if (appliedAny) {
        message.success(`${label} completed successfully.`);
      } else {
        message.info(`${label} completed, but no matching details were found.`);
      }
      return result;
    } catch (error) {
      message.error(error?.message || `${action.label || 'AI action'} failed`);
      return undefined;
    } finally {
      setLoadingKey(null);
    }
  }, [form, module, applyResponse]);

  return { runAction, loadingKey, busy: loadingKey !== null, isActionEnabled };
}
